import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { endOfMonth, format, subMonths } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PerformanceOverviewReport from "../PerformanceOverviewReport";

import { fetchCustomer } from "../../../api/customers";
import {
  getAdvancedMetricGraphData
} from "../../../api/expenseBreakdown";
import { getMetricGraphData, getMvpCashOnHand } from "../../../api/metric";
import {
  getSaasMetricCardSummary,
  getSaasMetricData,
  getSaasPlValues,
} from "../../../api/saas_api/metric";
import { useCurrentProfile } from "../../../ContextWrapper";
import { colorExpenseFormat } from "../../../utils";
import ClientDashboardLayout from "../ClientDashboardLayout";
import OnboardingBanner from "./performanceOnboardingBanner";
import { ScheduleCallModal } from "./performanceOverviewBookStrategyCall";
import { PerformanceOverviewSkeleton } from "./performanceSkeletons";

const theme = createTheme({
  typography: {
    fontFamily: "'Rethink Sans', 'Inter', sans-serif",
  },
  palette: {
    background: {
      default: "#F4F6FF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    primary: {
      main: "#536DEC",
      dark: "#4C63D7",
    },
  },
});

const emptySeries = Array.from({ length: 12 }, () => 0);

const goalOptions = [
  {
    id: "lean",
    title: "Lean",
    subtitle: "30 days of runway",
    days: 30,
    icon: "/Cash-Pay-1 Streamline Ux.svg",
  },
  {
    id: "balanced",
    title: "Balanced",
    subtitle: "90 days of runway",
    days: 90,
    icon: "/Balance-Equal-1 Streamline Ux.svg",
  },
  {
    id: "conservative",
    title: "Conservative",
    subtitle: "180 days of runway",
    days: 180,
    icon: "/Money-Safe-Open-1 Streamline Ux.svg",
  },
];

const metricCardDefs = [
  {
    title: "Team",
    type: "team",
    fallbackValue: 0,
    fallbackDelta: 0,
    fallbackDirection: "up",
    fallbackSeries: emptySeries,
  },
  {
    title: "Marketing",
    type: "marketing",
    fallbackValue: 0,
    fallbackDelta: 0,
    fallbackDirection: "up",
    fallbackSeries: emptySeries,
  },
  {
    title: "Operations",
    type: "overhead",
    fallbackValue: 0,
    fallbackDelta: 0,
    fallbackDirection: "up",
    fallbackSeries: emptySeries,
  },
  {
    title: "Products",
    type: "cogs",
    fallbackValue: 0,
    fallbackDelta: 0,
    fallbackDirection: "up",
    fallbackSeries: emptySeries,
  },
];

const currency = (value) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);

const fluid = (minPx, vw, maxPx) => `clamp(${minPx}px, ${vw}vw, ${maxPx}px)`;

const BODY_SCALE = 1.3;
const scalePx = (px) => Math.round(px * BODY_SCALE);
const scaleVw = (vw) => Number((vw * BODY_SCALE).toFixed(3));
const fluidBody = (minPx, vw, maxPx) =>
  fluid(scalePx(minPx), scaleVw(vw), scalePx(maxPx));

const axisMoney = (value) => {
  if (!value) return "$0";
  return `$${Math.round(value / 1000)}k`;
};

const toNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

const normalizeSignedCurrency = (value) => Math.abs(toNumber(value));

const formatMonthLabel = (date) => {
  const y = date.getFullYear();
  const m = date.toLocaleString("en-US", { month: "short" });
  return `${m} ${y}`;
};

const getPracticeHealthStatus = (perfectProfitPercent, profitPercent) => {
  const color = colorExpenseFormat(
    perfectProfitPercent,
    profitPercent,
    true,
    true,
  );
  if (color === "#68BA65") {
    return {
      label: "Positive",
      color: "#68BA65",
      bg: "rgba(104,186,101,0.18)",
    };
  }
  if (color === "#F1B20E") {
    return { label: "Warning", color: "#F1B20E", bg: "rgba(241,178,14,0.18)" };
  }
  if (color === "#FF5E5E") {
    return { label: "Caution", color: "#FF5E5E", bg: "rgba(255,94,94,0.18)" };
  }
  return { label: "Neutral", color: "#458CFF", bg: "rgba(69,140,255,0.18)" };
};

const getLastMonths = (count) => {
  const today = new Date();
  return Array.from({ length: count }).map(
    (_, i) =>
      new Date(today.getFullYear(), today.getMonth() - (count - 1 - i), 1),
  );
};

export function CashRunwayGoalModal({ open, onClose, onUpdate }) {
  const [selected, setSelected] = useState(goalOptions[0].id);

  const handleUpdate = () => {
    const selectedGoal = goalOptions.find((o) => o.id === selected);
    if (selectedGoal) onUpdate(selectedGoal);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", p: 0 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, p: "16px 24px" }}>
        Set Your Cash Runway Goal
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: "24px" }}>
        <Typography sx={{ color: "#6B7280", mb: "16px" }}>
          Choose a target number of days you’d like to keep available in cash.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          {goalOptions.map((option) => {
            const isSelected = selected === option.id;
            return (
              <Box
                key={option.id}
                onClick={() => setSelected(option.id)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "8px",
                  border: isSelected
                    ? "2px solid #536DEC"
                    : "1px solid #12162114",
                  backgroundColor: isSelected ? "#EEF0FD80" : "#FFF",
                  p: "16px",
                  textAlign: "center",
                  transition: "all 0.15s ease",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  "&:hover": { boxShadow: "0px 4px 8px 0px #0000001F" },
                }}
              >
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Box
                    component="img"
                    src={option.icon}
                    alt=""
                    sx={{ width: 84, height: 84 }}
                  />
                </Box>
                <Typography fontWeight={700} sx={{ fontSize: "16px" }}>
                  {option.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
                  {option.subtitle}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: "16px 20px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            borderRadius: "small",
            color: "#536DEC",
            fontSize: "0.875rem",
            borderColor: "#536DEC",
            p: "6px 16px",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          sx={{
            textTransform: "none",
            borderRadius: "small",
            fontSize: "0.875rem",
            bgcolor: "#536DEC",
            p: "6px 16px",
            "&:hover": { bgcolor: "#465EDB" },
            "&:active": { bgcolor: "#3E55CC" },
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Sparkline = ({
  data = [],
  labels = [],
  color = "#635BFF",
  height = 80,
}) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return data.map((value, i) => ({
      x: labels?.[i] ?? `Point ${i + 1}`,
      value,
    }));
  }, [data, labels]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const dataPoint = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          px: 1,
          py: 1,
          borderRadius: "6px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          fontSize: 12,
          fontWeight: 500, // base weight for label
          pointerEvents: "none",
          fontFamily: '"Rethink Sans"',
        }}
      >
        {dataPoint.x}:{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {currency(dataPoint.value)}
        </Box>
      </Box>
    );
  };

  const CustomActiveDot = (props) => {
    const { cx, cy, stroke, fill } = props;
    return (
      <g>
        <circle cx={cx} cy={cy} r={5 + 3} fill="#fff" />
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill={fill}
          stroke={stroke}
          strokeWidth={0}
        />
      </g>
    );
  };

  if (!chartData.length) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        "& svg:focus, g:focus, path:focus": {
          outline: "none !important",
        },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 6, right: 0, left: 0, bottom: 6 }}
        >
          <defs>
            <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(99, 91, 255, 0.2)" />
              <stop offset="100%" stopColor="rgba(99, 91, 255, 0)" />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill="url(#sparklineGradient)"
            baseValue="dataMin"
          />
          <XAxis dataKey="x" hide />
          <YAxis hide />
          <Tooltip cursor={false} content={<CustomTooltip />} />

          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={<CustomActiveDot fill={color} stroke={color} />}
            strokeLinecap="round"
            fill="url(#sparklineGradient)"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

const MetricCard = ({
  title,
  value,
  delta,
  direction,
  series,
  labels,
  onClick,
}) => {
  // These tiles represent expense categories: down is good (green), up is bad (red).
  const isGood = direction === "down";
  const deltaColor = isGood ? "#388E3C" : "#E53935";
  const deltaBg = isGood ? "#E6F9F2" : "rgba(229,57,53,0.12)";
  const arrow = direction === "down" ? "↓" : "↑";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "10px",
        boxShadow: "#00000013",
        border: "1px solid rgba(0,0,0,0.05)",
        backgroundColor: "#FFFFFF",
        minHeight: "172px",
        cursor: onClick ? "pointer" : "default",
        "&:hover": { boxShadow: "0px 1px 16px 0px #0000001A" },

        "&:hover .metric-value": {
          transform: "scale(1.20)",
          mr: "10px",
        },

        "&:hover .metric-icon": {
          transform: "rotate(45deg) scale(1.20)",
          color: "#4C63D7",
        },
      }}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick) {
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(event);
        }
      }}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{
              color: "rgba(23,25,35,0.6)",
              fontFamily: '"Rethink Sans"',
              // Match Business Health "Profit" label styling.
              fontSize: "1rem",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "150%",
              letterSpacing: "0.15px",
            }}
          >
            {title}
          </Typography>
          <ArrowUpwardIcon
            className="metric-icon"
            sx={{
              width: 25,
              height: 25,
              color: "#0000008F",
              transform: "rotate(45deg) scale(1)",
              transformOrigin: "center",
              transition: "transform 160ms ease-out",
            }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Typography
            className="metric-value"
            sx={{
              fontSize: "24px",
              fontWeight: 700,
              color: "rgba(23,25,35,0.9)",
              lineHeight: 1.2,
              display: "inline-block", // required for transform
              transform: "scale(1)",
              transformOrigin: "left center",
              transition: "transform 160ms ease-out",
            }}
          >
            {currency(value)}
          </Typography>
          <Chip
            label={`${arrow} ${Math.abs(delta)}%`}
            size="small"
            sx={{
              color: deltaColor,
              bgcolor: deltaBg,
              fontFamily: '"Rethink Sans"',
              fontWeight: 400,
              fontSize: "14px",
              height: "25px",
              borderRadius: "6px",
              p: "2px 5px",
              "& .MuiChip-label": {
                px: 0,
                letterSpacing: "0.5px",
              },
            }}
          />
        </Stack>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "stretch",
            mt: 0,
            mx: -2,
            mb: -2,
          }}
        >
          <Sparkline
            data={series}
            labels={labels}
            color="#536DEC"
            height={82}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const PendingBar = ({ label, value, color, maxValue, tone = "positive" }) => {
  const width = Math.min(100, (value / maxValue) * 100 || 0);
  const isNegative = tone === "negative";
  const barFillColor = isNegative ? color : "#536DEC";
  const barTrackColor = isNegative
    ? "rgba(229, 90, 90, 0.16)"
    : "rgba(83, 109, 236, 0.16)";
  return (
    <Stack spacing={1.1} sx={{ alignSelf: "stretch", mt: 1 }}>
      <Typography
        sx={{
          color: "rgba(23, 25, 35, 0.60)",
          fontFamily: '"Rethink Sans"',
          fontSize: "13px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: fluidBody(13.72, 0.95, 17),
          letterSpacing: "0.286px",
        }}
      >
        {label}
      </Typography>
      <Box display="flex" alignItems="center" width="100%">
        {/* Left side: value label, fixed 30% width */}
        <Box
          flex="0 0 25%"
          textAlign="left"
          pr={1} // optional spacing between label and bar
        >
          <Typography
            sx={{
              fontSize: fluidBody("20px"),
              lineHeight: 1.2,
              color: "#111827",
              fontWeight: 600,
            }}
          >
            {currency(value)}
          </Typography>
        </Box>

        {/* Right side: bar, fixed 70% width */}
        <Box
          flex="0 0 75%"
          height="16px"
          borderRadius={999}
          bgcolor={barTrackColor}
          overflow="hidden"
        >
          <Box
            sx={{
              height: "100%",
              width: `${width}%`,
              borderRadius: 999,
              bgcolor: barFillColor,
            }}
          />
        </Box>
      </Box>
    </Stack>
  );
};

const FinancialTrendChart = ({ range, metricGraphData, anchorDate }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  /* ----------------------------- helpers ----------------------------- */

  const rangeCount = (range) => {
    switch (range) {
      case "7 Days":
        return 7;
      case "30 Days":
        return 30;
      case "6 Months":
        return 6;
      case "12 Months":
      default:
        return 12;
    }
  };

  /* -------------------------- normalize data -------------------------- */

  const chartData = useMemo(() => {
    const defaultLabels = (range) => {
      const count = rangeCount(range);

      if (range === "7 Days" || range === "30 Days") {
        return Array.from({ length: count }, (_, i) => `Day ${i + 1}`);
      }

      // Use anchorDate (selected period end) so fallback labels match the requested window.
      const anchor =
        anchorDate instanceof Date && !isNaN(anchorDate)
          ? anchorDate
          : new Date();
      return Array.from({ length: count }, (_, i) => {
        const d = new Date(
          anchor.getFullYear(),
          anchor.getMonth() - (count - 1 - i),
          1,
        );
        return d.toLocaleString("default", { month: "short" });
      });
    };

    const hasRealData =
      metricGraphData?.months?.length &&
      metricGraphData.months.some((_, i) => {
        return (
          Number(metricGraphData.income?.[i] ?? 0) !== 0 ||
          Number(metricGraphData.expense?.[i] ?? 0) !== 0 ||
          Number(metricGraphData.profit?.[i] ?? 0) !== 0
        );
      });

    if (!hasRealData) {
      return defaultLabels(range).map((label) => ({
        label,
        income: 0,
        expenses: 0,
        profit: 0,
      }));
    }

    const allPoints = metricGraphData.months.map((label, i) => {
      const income = normalizeSignedCurrency(metricGraphData.income?.[i] ?? 0);
      const expenses = normalizeSignedCurrency(
        metricGraphData.expense?.[i] ?? 0,
      );
      const profit = income - expenses;

      return {
        label,
        income,
        expenses,
        profit,
      };
    });

    // For month-based ranges, guard against the API returning more months than
    // the selected view (e.g. a stale cache returning 12 months when 6 were
    // requested). Always take the most-recent N months.
    const count = rangeCount(range);
    if (range === "6 Months" || range === "12 Months") {
      return allPoints.slice(-count);
    }
    return allPoints;
  }, [metricGraphData, range, anchorDate]);

  /* ------------------------ clip data for visuals --------------------- */
  // Bar heights are clipped to ≥0, but the unclipped originals are embedded
  // as rawIncome/rawExpenses/rawProfit so the tooltip can read them directly
  // from payload[0].payload without an index lookup.
  const renderedChartData = chartData.map((d) => ({
    ...d,
    income: Math.max(d.income, 0),
    expenses: Math.max(d.expenses, 0),
    profit: Math.max(d.profit, 0),
    rawIncome: d.income,
    rawExpenses: d.expenses,
    rawProfit: d.profit,
  }));

  /* ---------------------------- tooltip ---------------------------- */

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    // Read the real (unclipped) values embedded directly in the data point.
    const pt = payload[0].payload;
    const data = {
      label: pt.label,
      income: pt.rawIncome ?? pt.income,
      expenses: pt.rawExpenses ?? pt.expenses,
      profit: pt.rawProfit ?? pt.profit,
    };

    const Row = ({ color, label, value, isLine = false }) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: 12,
        }}
      >
        {isLine ? (
          <Box
            sx={{
              width: 14,
              height: 3,
              borderRadius: 2,
              bgcolor: color,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: color,
            }}
          />
        )}

        <Typography sx={{ color: "#6B7280", flex: 1, fontSize: 14 }}>
          {label}:
        </Typography>

        <Typography
          sx={{
            fontWeight: 500,
            color:
              label === "Profit"
                ? value >= 0
                  ? "#00B050"
                  : "#E53935"
                : "#111827",
            fontSize: 14,
          }}
        >
          {currency(value)}
        </Typography>
      </Box>
    );

    return (
      <Box
        sx={{
          bgcolor: "#fff",
          px: 2,
          py: 1.5,
          borderRadius: 2,
          boxShadow: "0px 10px 25px rgba(0,0,0,0.15)",
          border: "1px solid rgba(0,0,0,0.06)",
          minWidth: 180,
        }}
      >
        {data.label && (
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: "#111827", mb: 0.75 }}
          >
            {data.label}
          </Typography>
        )}
        <Box sx={{ mb: 0.5 }}>
          <Row color="#536DEC" label="Income" value={data.income} />
        </Box>
        <Row color="#E53935" label="Expenses" value={data.expenses} />
        <Divider sx={{ my: 0.5 }}></Divider>
        <Row color="#00B050" label="Profit" value={data.profit} isLine />
      </Box>
    );
  };

  const CustomCursor = ({ x, y, width, height }) => {
    const offset = (width - width) / 2;

    const topRadius = 12;

    const d = `
      M${x + offset + topRadius},${y}
      H${x + offset + width - topRadius}
      A${topRadius},${topRadius} 0 0 1 ${x + offset + width},${y + topRadius}
      V${y + height}
      H${x + offset}
      V${y + topRadius}
      A${topRadius},${topRadius} 0 0 1 ${x + offset + topRadius},${y}
      Z
    `;

    return <path d={d} fill="#536DEC" fillOpacity={0.14} />;
  };

  /* ---------------------------- render ---------------------------- */

  return (
    <Box sx={{ width: "100%", height: "100%", minWidth: 0 }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          "& svg:focus, g:focus, path:focus": { outline: "none !important" },
        }}
      >
        <ResponsiveContainer
          width="100%"
          height="90%"
          style={{ outline: "none" }}
        >
          <BarChart
            data={renderedChartData}
            barCategoryGap={"20%"}
            barGap={4}
            margin={{ top: 20, right: 20, left: 10, bottom: 0 }}
            onMouseMove={(state) => {
              if (!state.isTooltipActive) {
                setHoveredIndex(null);
                return;
              }

              const index = renderedChartData.findIndex(
                (d) => d.label === state.activeLabel,
              );
              setHoveredIndex(index);
            }}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <CartesianGrid
              strokeDasharray="4 4"
              vertical={false}
              stroke="rgba(0,0,0,0.15)"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 14 }}
              interval="preserveStartEnd"
              allowDuplicatedCategory={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, "auto"]}
              tickFormatter={axisMoney}
              tick={{ fontSize: 14 }}
              width={56}
            />
            <Tooltip content={<CustomTooltip />} cursor={<CustomCursor />} />

            {/* BARS WITH DYNAMIC OPACITY */}
            <Bar
              dataKey="income"
              fill="#536DEC"
              radius={[4, 4, 0, 0]}
              shape={(props) => {
                const barIndex = props.payload.index ?? props.index;
                const opacity =
                  hoveredIndex === null || hoveredIndex === barIndex ? 1 : 0.5;
                return <Rectangle {...props} fillOpacity={opacity} />;
              }}
            />
            <Bar
              dataKey="expenses"
              fill="#E53935"
              radius={[4, 4, 0, 0]}
              shape={(props) => {
                const barIndex = props.payload.index ?? props.index;
                const opacity =
                  hoveredIndex === null || hoveredIndex === barIndex ? 1 : 0.5;
                return <Rectangle {...props} fillOpacity={opacity} />;
              }}
            />

            {/* LINE STAYS VISIBLE */}
            <Line
              type="linear"
              dataKey="profit"
              stroke="#00B050"
              strokeWidth={3}
              dot={false}
            />
          </BarChart>
        </ResponsiveContainer>

        {/* LEGEND */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 5, mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#536DEC" }} />
            <Typography sx={{ fontSize: 14 }}>Income</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#E53935" }} />
            <Typography sx={{ fontSize: 14 }}>Expenses</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 20, height: 3, bgcolor: "#00B050" }} />
            <Typography sx={{ fontSize: 14 }}>Profit</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ActivityPulse = ({ data }) => {
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [totalDays, setTotalDays] = useState(45);

  const lastUpdated = data?.lastUpdated ?? "—";
  const cashOnHandDays = data?.days ?? 0;
  const totalBalance = data?.balance ?? 0;
  const avgBurn = data?.avgExpense ?? 0;
  const progressDays = Math.min(cashOnHandDays, totalDays);

  const showSetGoalButton = false;
  const progress = Math.min(100, (progressDays / totalDays) * 100);

  const size = 200;
  const thickness = 18;
  const pillWidth = 2;
  const pillHeight = thickness;

  const radius = size / 2 - pillHeight / 2;
  const angleStep = 360 / totalDays;

  const handleGoalUpdate = (selectedGoal) => {
    setTotalDays(selectedGoal.days);
    setGoalModalOpen(false);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "12px",
        boxShadow: "#00000013",
        border: "1px solid #E8EBF2",
        backgroundColor: "#FFFFFF",
        height: "100%",
        minHeight: { xs: "320px", sm: "380px", md: "449px" },
        minWidth: 0,
        width: "100%",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            {/* Text block */}
            <Box>
              <Typography
                sx={{
                  color: "#474747",
                  fontFamily: '"Rethink Sans"',
                  fontSize: "20px",
                  fontStyle: "normal",
                  fontWeight: 700,
                  lineHeight: "28.8px",
                  letterSpacing: "0.5px",
                  mb: 0.5,
                }}
              >
                Cash On Hand
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans"',
                  fontSize: "0.875rem",
                  color: "#6B7280",
                }}
              >
                Last updated{" "}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#6B7280",
                  }}
                >
                  {lastUpdated}
                </Typography>
              </Typography>
            </Box>

            {/* Outline button */}
            {showSetGoalButton && (
              <Button
                variant="outlined"
                onClick={() => setGoalModalOpen(true)}
                endIcon={
                  <ArrowUpwardIcon
                    sx={{
                      width: 16,
                      height: 16,

                      transform: "rotate(45deg) scale(1)",
                    }}
                  />
                }
                sx={{
                  height: 36,
                  px: 2,
                  py: 0,
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#536DEC",
                  color: "#536DEC",
                  fontFamily: '"Rethink Sans"',
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  lineHeight: 1,
                  ariaLabel: "Set runway goal",
                  "&:hover": {
                    borderColor: "#536DEC",
                    bgcolor: "rgba(83, 109, 236, 0.04)",
                  },
                }}
              >
                Set Goal
              </Button>
            )}
          </Box>
        </Stack>

        <Stack alignItems="center">
          <Box sx={{ position: "relative", width: size, height: size }}>
            <svg
              width={size}
              height={size}
              style={{ position: "absolute", top: 0, left: 0 }}
            >
              {Array.from({ length: totalDays }).map((_, i) => {
                const angle = i * angleStep - 90;
                const rad = (angle * Math.PI) / 180;
                const cx = size / 2 + radius * Math.cos(rad);
                const cy = size / 2 + radius * Math.sin(rad);

                return (
                  <rect
                    key={i}
                    x={cx - pillWidth / 2}
                    y={cy - pillHeight / 2}
                    width={pillWidth}
                    height={pillHeight}
                    fill="#E5E7EB"
                    rx={pillWidth / 2}
                    ry={pillWidth / 2}
                    transform={`rotate(${angle + 90} ${cx} ${cy})`}
                  />
                );
              })}
            </svg>

            <CircularProgress
              variant="determinate"
              value={-progress}
              size={size}
              thickness={4}
              sx={{
                color: "#536DEC",
                position: "absolute",
                left: 0,
                top: 0,
                circle: { strokeLinecap: "round" },
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    color: "rgba(0,0,0,0.6)",
                    fontWeight: 600,
                  }}
                >
                  Est. Runway
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography
                  sx={{ fontSize: 36, fontWeight: 700, color: "#536DEC" }}
                >
                  {progressDays}
                </Typography>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: 700, color: "#00000061" }}
                >
                  /{totalDays} Days
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>

        <Stack spacing={2}>
          {/* Total Bank Balance */}
          <Box
            sx={{
              bgcolor: "#f8f9fc",
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: "42px",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: "13px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              Total Bank Balance
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: "1rem",
                fontWeight: 600,
                color: "rgba(0,0,0,0.87)",
              }}
            >
              {currency(totalBalance)}
            </Typography>
          </Box>

          {/* Avg Monthly Burn Rate */}
          <Box
            sx={{
              bgcolor: "#f8f9fc",
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: "42px",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: "13px",
                color: "rgba(0,0,0,0.6)",
              }}
            >
              Avg. Monthly Burn Rate
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Rethink Sans"',
                fontSize: "1rem",
                fontWeight: 600,
                color: "rgba(0,0,0,0.87)",
              }}
            >
              {currency(avgBurn)}
            </Typography>
          </Box>
        </Stack>
        <CashRunwayGoalModal
          open={goalModalOpen}
          onClose={() => setGoalModalOpen(false)}
          onUpdate={handleGoalUpdate}
        />
      </CardContent>
    </Card>
  );
};

const PracticeHealthCard = ({ summary }) => {
  const maxValue = Math.max(summary.income, summary.expenses);
  const hasStatus = summary?.hasData;
  const statusLabel = hasStatus
    ? (summary.statusLabel ?? "Neutral")
    : "No Status";
  const statusColor = hasStatus
    ? (summary.statusColor ?? "#458CFF")
    : "#6B7280";
  const statusBg = hasStatus
    ? (summary.statusBg ?? "rgba(69,140,255,0.18)")
    : "rgba(156,163,175,0.18)";
  const changeValue = toNumber(summary?.change);
  const ChangeIcon = changeValue >= 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  const changeColor = hasStatus
    ? changeValue >= 0
      ? "#00B050"
      : "#D9534F"
    : "#6B7280";
  const changeLabel = hasStatus ? `${Math.abs(changeValue).toFixed(1)}%` : "—";
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "12px",
        boxShadow: "#00000013",
        border: "1px solid #E2E6EF",
        backgroundColor: "#FFFFFF",
        height: "100%",
        minHeight: "368px",
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: "24px 20px",
          justifyContent: "space-between",
          minHeight: "368px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{
              color: "#474747",
              fontFamily: '"Rethink Sans"',
              fontSize: "20px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "28.8px",
              letterSpacing: "0.5px",
            }}
          >
            Business Health
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: statusBg,
              color: statusColor,
              height: { xs: "32px", lg: "32px" },
              borderRadius: 2,
              px: 0.75,
              "& .MuiChip-label": {
                color: statusColor,
                fontSize: scalePx("14px"),
                fontStyle: "normal",
                fontFamily: '"Rethink Sans"',
                fontWeight: 400,
                lineHeight: "21px",
                letterSpacing: "0.5px",
              },
            }}
          />
        </Stack>
        <Stack spacing={"0px"}>
          <Typography
            sx={{
              alignSelf: "stretch",
              color: "rgba(23, 25, 35, 0.60)",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: '"Rethink Sans"',
              fontSize: "1rem",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "150%",
              letterSpacing: "0.15px",
            }}
          >
            Profit
          </Typography>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography
              sx={{
                color: "rgba(23, 25, 35, 0.90)",
                fontFamily: '"Rethink Sans"',
                fontSize: "36px",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: 1.2556,
              }}
            >
              {currency(summary.profit)}
            </Typography>
            <Box
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <ChangeIcon
                sx={{
                  fontSize: scalePx("14px"),
                  color: changeColor,
                  position: "relative",
                  mt: -0.5,
                }}
              />
              <Typography
                sx={{
                  color: changeColor,
                  fontFamily: '"Rethink Sans"',
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "21px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  letterSpacing: "0.5px",
                }}
              >
                {changeLabel}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 0 }} />

        <Stack
          gap={2}
          sx={{
            width: "100%",
            margin: "0 auto",
          }}
        >
          <PendingBar
            label="Income"
            value={summary.income}
            tone="positive"
            color="#4F64D4"
            maxValue={maxValue}
          />
          <PendingBar
            label="Expenses"
            value={summary.expenses}
            tone="negative"
            color="#E65A5A"
            maxValue={maxValue}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const RangeSelector = ({ active, onChange }) => {
  const ranges = ["12 Months", "6 Months", "30 Days", "7 Days"];
  return (
    <Stack direction="row" spacing={2.5} alignItems="center">
      {ranges.map((range) => (
        <ButtonBase
          key={range}
          onClick={() => onChange?.(range)}
          sx={{
            borderRadius: "8px",
            px: active === range ? 1.625 : 0.975,
            py: 0.65,
            border:
              active === range
                ? "1px solid rgba(0,0,0,0.87)"
                : "1px solid transparent",
            bgcolor: "transparent",
            p: "6px 16px",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Rethink Sans"',
              fontSize: `${scalePx(13)}px`,
              fontWeight: 500,
              lineHeight: 1,
              letterSpacing: "0.46px",
              color: active === range ? "rgba(0,0,0,0.87)" : "rgba(0,0,0,0.60)",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
            }}
          >
            {range}
          </Typography>
        </ButtonBase>
      ))}
    </Stack>
  );
};

const PerformanceOverview = ({ setProfile }) => {
  const navigate = useNavigate();
  const params = useParams();
  const { profile } = useCurrentProfile();
  const reportRef = useRef(null);
  const practiceHealthEffectActiveRef = useRef(true);
  const [storedCustomerId, setStoredCustomerId] = useState(() => {
    try {
      return sessionStorage.getItem("selectedCustomerId");
    } catch (error) {
      return null;
    }
  });

  const { customer_id, true_customer_id, isDemoCustomer } = useMemo(() => {
    const customers = Array.isArray(profile?.customers)
      ? profile?.customers
      : [];
    // Both admin and accountant are staff users who view customer data
    // to refine it — demo substitution must never apply to either.
    const isStaff = profile?.type === "admin" || profile?.type === "accountant";
    const routeId = params?.customer_id ? String(params.customer_id) : null;

    // Staff users (admin / accountant) always see a customer's live data.
    // Priority order:
    //   1. URL route param — most reliable, set by sideNav navigation
    //   2. localStorage selectedCustomerId — set when switching customers
    //   3. First customer on the profile (rare fallback)
    // profile.customers is intentionally empty for staff (they manage many
    // customers and have no pre-scoped list), so we must not rely on it here.
    if (isStaff) {
      const staffCustomerId =
        routeId || storedCustomerId || customers[0]?.id?.toString() || null;
      return {
        customer_id: staffCustomerId,
        true_customer_id: staffCustomerId,
        isDemoCustomer: false,
      };
    }

    if (!customers.length) {
      return {
        customer_id: null,
        true_customer_id: null,
        isDemoCustomer: true,
      };
    }

    const demoCustomers = customers.filter((c) => c?.is_demo);
    const nonDemoCustomers = customers.filter((c) => !c?.is_demo);

    const hasTrialLike = nonDemoCustomers.some((c) =>
      ["Trial Demo", "Unpublished"].includes(c?.frontend_account_type),
    );

    const trueId =
      storedCustomerId || nonDemoCustomers[0]?.id || customers[0]?.id || null;

    let effectiveId = trueId;

    if (hasTrialLike && demoCustomers[0]?.id) {
      effectiveId = demoCustomers[0].id;
    }

    const current = customers.find((c) => c.id === effectiveId);

    return {
      customer_id: effectiveId,
      true_customer_id: trueId,
      isDemoCustomer: current?.is_demo === true,
    };
  }, [profile, storedCustomerId, params?.customer_id]);

  const routeCustomerId = useMemo(() => {
    const raw = params?.customer_id;
    return raw ? String(raw) : null;
  }, [params?.customer_id]);

  const [routeCustomerDetails, setRouteCustomerDetails] = useState(null);
  useEffect(() => {
    if (!routeCustomerId) {
      setRouteCustomerDetails(null);
      return;
    }

    let active = true;
    void (async () => {
      try {
        const res = await fetchCustomer(routeCustomerId);
        if (!active) return;
        setRouteCustomerDetails(res || null);
      } catch (err) {
        if (!active) return;
        setRouteCustomerDetails(null);
      }
    })();

    return () => {
      active = false;
    };
  }, [routeCustomerId]);

  const showCashOnHand = useMemo(() => {
    const routeAccountType = String(
      routeCustomerDetails?.frontend_account_type ?? "",
    )
      .trim()
      .toLowerCase();
    if (routeAccountType) return routeAccountType !== "published";

    const customers = Array.isArray(profile?.customers)
      ? profile.customers
      : [];

    const resolvedCustomer =
      customers.find((c) => String(c.id) === String(true_customer_id)) ||
      customers.find((c) => String(c.id) === String(customer_id)) ||
      null;

    const accountType = String(resolvedCustomer?.frontend_account_type ?? "")
      .trim()
      .toLowerCase();

    if (!accountType) return true;
    return accountType !== "published";
  }, [
    profile?.customers,
    true_customer_id,
    customer_id,
    routeCustomerDetails?.frontend_account_type,
  ]);

  const [isYearToggled, setIsYearToggled] = useState(() => {
    try {
      const stored = sessionStorage.getItem("performanceYtd");
      if (stored === null) return true; // default ON
      return stored === "true";
    } catch (error) {
      return true;
    }
  });

  // End-of-month date for the month selected in the "Month" tab.
  // Defaults to the last completed month if nothing is stored.
  const [selectedMonthEnd, setSelectedMonthEnd] = useState(() => {
    try {
      const stored = sessionStorage.getItem("performanceSelectedMonth");
      if (stored) {
        const d = new Date(stored);
        if (!isNaN(d)) return d;
      }
    } catch {
      /* ignore */
    }
    return endOfMonth(subMonths(new Date(), 1));
  });
  const [metricGraphData, setMetricGraphData] = useState({
    income: [],
    expense: [],
    profit: [],
    months: [],
  });
  // Monthly graph snapshot dedicated for Business Health anchoring so it doesn't
  // change when the Financial Trend range switches to 7d/30d.
  const [metricGraphMonthly, setMetricGraphMonthly] = useState({
    income: [],
    expense: [],
    profit: [],
    months: [],
  });
  const [practiceHealthData, setPracticeHealthData] = useState({
    profit: 0,
    change: 0,
    income: 0,
    expenses: 0,
    incomePending: 0,
    expensePending: 0,
    statusLabel: "Neutral",
    statusColor: "#458CFF",
    statusBg: "rgba(69,140,255,0.18)",
    hasData: false,
  });
  const [cashOnHandData, setCashOnHandData] = useState({
    days: 0,
    balance: 0,
    avgExpense: 0,
    statusLabel: "Bad Position",
    color: "#d9534f",
    bg: "rgba(217,83,79,0.12)",
  });
  const [advancedMetrics, setAdvancedMetrics] = useState({});
  const [trendRange, setTrendRange] = useState("12 Months");
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // For "Past 12 Months" mode, anchor to the last completed month (not today).
  const lastCompletedMonthEnd = useMemo(
    () => endOfMonth(subMonths(new Date(), 1)),
    [],
  );

  // "Month" mode uses the picker selection; "Past 12 Months" uses last completed month.
  const dashboardMonthEnd = useMemo(
    () => (isYearToggled ? lastCompletedMonthEnd : selectedMonthEnd),
    [isYearToggled, lastCompletedMonthEnd, selectedMonthEnd],
  );

  const dashboardDateStr = useMemo(
    () => format(dashboardMonthEnd, "yyyy-MM-dd"),
    [dashboardMonthEnd],
  );

  const handleTileClick = (title) => {
    const descriptor = String(title || "").toLowerCase();
    const isStaff =
      profile?.type === "admin" || profile?.type === "accountant";
    if (isStaff && customer_id) {
      navigate(`/client/${customer_id}/performance/${descriptor}`);
    } else {
      navigate(`/performance/${descriptor}`);
    }
  };

  const handleExportReport = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Performance Overview Report ${dashboardDateStr}`,
    pageStyle: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
  });

  useEffect(() => {
    const syncFromStorage = () => {
      try {
        setStoredCustomerId(sessionStorage.getItem("selectedCustomerId"));
      } catch (error) {
        setStoredCustomerId(null);
      }
    };

    window.addEventListener("selectedCustomerId-changed", syncFromStorage);
    return () =>
      window.removeEventListener("selectedCustomerId-changed", syncFromStorage);
  }, []);

  useEffect(() => {
    const syncYtd = () => {
      try {
        const stored = sessionStorage.getItem("performanceYtd");
        if (stored === null) {
          setIsYearToggled(true);
        } else {
          setIsYearToggled(stored === "true");
        }
        // Sync the selected month whenever the toggle event fires
        const monthStored = sessionStorage.getItem("performanceSelectedMonth");
        if (monthStored) {
          const d = new Date(monthStored);
          if (!isNaN(d)) setSelectedMonthEnd(d);
        }
      } catch (error) {
        setIsYearToggled(true);
      }
    };

    window.addEventListener("performance-ytd-changed", syncYtd);
    return () => window.removeEventListener("performance-ytd-changed", syncYtd);
  }, []);

  useEffect(() => {
    if (!customer_id) return;
    const controller = new AbortController();
    const fetchMetricGraph = async () => {
      try {
        const period =
          trendRange === "7 Days"
            ? "7d"
            : trendRange === "30 Days"
              ? "30d"
              : trendRange === "6 Months"
                ? "6m"
                : undefined;
        const res = await getMetricGraphData(
          customer_id,
          dashboardDateStr,
          controller.signal,
          {
            adjusted: false,
            ytd: false,
            ...(period ? { period } : {}),
          },
        );
        if (res?.data) {
          setMetricGraphData(res.data);
        }
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.warn("Failed to load metric graph data", err);
        }
      }
    };
    fetchMetricGraph();
    return () => controller.abort();
  }, [customer_id, trendRange, dashboardDateStr]);

  useEffect(() => {
    if (!customer_id) {
      setDashboardLoading(false);
      return;
    }
    practiceHealthEffectActiveRef.current = true;
    setDashboardLoading(true);
    const controller = new AbortController();
    const fetchPracticeHealth = async () => {
      try {
        const res = await getSaasMetricData(
          customer_id,
          dashboardDateStr,
          controller.signal,
          { adjusted: false, year: isYearToggled },
        );
        const data = res?.data || {};
        const hasMetricData = Boolean(
          res?.data && Object.keys(res.data).length,
        );
        let profitPercent = data.profit_percentage ?? 0;
        let perfectProfitPercent;

        try {
          const plRes = await getSaasPlValues(
            customer_id,
            dashboardDateStr,
            controller.signal,
            { adjusted: false, ytd: isYearToggled },
          );
          perfectProfitPercent = plRes?.data?.profit_pl;
        } catch (growthErr) {
          if (
            growthErr?.name !== "CanceledError" &&
            growthErr?.name !== "AbortError"
          ) {
            console.warn("Failed to load Perfect P&L values", growthErr);
          }
        }
        const status = getPracticeHealthStatus(
          perfectProfitPercent,
          profitPercent,
        );
        const income = normalizeSignedCurrency(data.income);
        const expenses = normalizeSignedCurrency(data.expense);
        setPracticeHealthData({
          profit: income - expenses,
          change: profitPercent ?? data.profit_percentage ?? 0,
          income,
          expenses,
          incomePending: data.income_pending ?? 0,
          expensePending: data.expense_pending ?? 0,
          statusLabel: status.label,
          statusColor: status.color,
          statusBg: status.bg,
          hasData: hasMetricData,
        });
        if (practiceHealthEffectActiveRef.current) setDashboardLoading(false);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.warn("Failed to load business health summary", err);
        }
        if (practiceHealthEffectActiveRef.current) setDashboardLoading(false);
      }
    };
    fetchPracticeHealth();
    return () => {
      practiceHealthEffectActiveRef.current = false;
      controller.abort();
    };
  }, [customer_id, dashboardDateStr, isYearToggled]);

  // Cash on hand intentionally stays "current month" regardless of YTD toggle.
  useEffect(() => {
    if (!customer_id) return;
    if (!showCashOnHand) return;
    const controller = new AbortController();
    const fetchCashOnHand = async () => {
      try {
        const monthKey = format(new Date(), "yyyy-MM");
        const res = await getMvpCashOnHand(customer_id, controller.signal, {
          month: monthKey,
        });
        const entry = res?.data || {};
        const avgExpense = Math.max(0, toNumber(entry.avg_monthly_burn));
        const balance = Math.max(0, toNumber(entry.total_bank_balance));
        const days = avgExpense ? Math.round((balance / avgExpense) * 30) : 0;
        const noPosition = !entry || !Object.keys(entry).length;
        const cashStatus = noPosition
          ? {
              statusLabel: "No Position",
              color: "#9CA3AF",
              bg: "rgba(156,163,175,0.18)",
            }
          : days <= 30
            ? {
                statusLabel: "Bad Position",
                color: "#d9534f",
                bg: "rgba(217,83,79,0.12)",
              }
            : days < 60
              ? {
                  statusLabel: "Average Position",
                  color: "#f59e0b",
                  bg: "rgba(245,158,11,0.18)",
                }
              : {
                  statusLabel: "Good Position",
                  color: "#4c63d7",
                  bg: "rgba(76,99,215,0.12)",
                };

        setCashOnHandData({
          days,
          balance,
          avgExpense,
          lastUpdated: entry.lastUpdated ?? "—",
          ...cashStatus,
        });
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.warn("Failed to load cash on hand summary", err);
        }
      }
    };
    fetchCashOnHand();
    return () => controller.abort();
  }, [customer_id, showCashOnHand]);

  useEffect(() => {
    if (!customer_id) return undefined;
    const controller = new AbortController();
    const date = dashboardDateStr;
    const types = metricCardDefs.map((card) => card?.type);

    Promise.all(
      types.map(async (type) => {
        try {
          const summaryPromise = getSaasMetricCardSummary(
            customer_id,
            date,
            type,
            controller.signal,
            { adjusted: false, year: isYearToggled },
          );

          const trendPromise = getAdvancedMetricGraphData(
            customer_id,
            date,
            type,
            controller.signal,
            {},
          );

          const [summaryRes, trendRes] = await Promise.all([
            summaryPromise,
            trendPromise,
          ]);

          const trendData = (() => {
            if (!trendRes?.data) return null;
            return trendRes.data;
          })();

          return { type, summary: summaryRes?.data || null, trend: trendData };
        } catch (err) {
          if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
            console.warn("Failed to load advanced metric data", type, err);
          }
          return { type, summary: null, trend: null };
        }
      }),
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const next = {};
        results.forEach((res) => {
          if (res?.type) {
            next[res.type] = { summary: res.summary, trend: res.trend };
          }
        });
        setAdvancedMetrics(next);
      })
      .catch(() => {})
      .finally(() => {});

    return () => controller.abort();
  }, [customer_id, dashboardDateStr, dashboardMonthEnd, isYearToggled]);

  const metricCards = useMemo(
    () =>
      metricCardDefs.map((def) => {
        const summary = advancedMetrics[def.type]?.summary;
        const trend = advancedMetrics[def.type]?.trend;

        const valueRaw = isYearToggled
          ? (summary?.actual ??
            summary?.current_month_total ??
            summary?.pnl_value ??
            def.fallbackValue)
          : (summary?.current_month_total ??
            summary?.actual ??
            summary?.pnl_value ??
            def.fallbackValue);
        const changeRaw = isYearToggled
          ? (summary?.change_from_ytd_avg_pct ??
            summary?.change_from_last_month_pct ??
            summary?.change_from_last_month ??
            def.fallbackDelta)
          : (summary?.change_from_last_month_pct ??
            summary?.change_from_last_month ??
            def.fallbackDelta);

        const deltaNum = Number(changeRaw);
        const hasDelta = Number.isFinite(deltaNum);
        const direction = hasDelta
          ? deltaNum >= 0
            ? "up"
            : "down"
          : def.fallbackDirection;
        const delta = hasDelta
          ? Math.abs(deltaNum)
          : Math.abs(def.fallbackDelta);

        const series = (() => {
          if (!isYearToggled) {
            const seriesRaw = Array.isArray(trend?.values)
              ? trend.values.map((v) => toNumber(v))
              : def.fallbackSeries;
            return seriesRaw && seriesRaw.length
              ? seriesRaw
              : def.fallbackSeries;
          }
          const seriesRaw = Array.isArray(trend?.values)
            ? trend.values.map((v) => toNumber(v))
            : def.fallbackSeries;
          return seriesRaw && seriesRaw.length ? seriesRaw : def.fallbackSeries;
        })();
        const labels =
          Array.isArray(trend?.labels) && trend.labels.length === series.length
            ? trend.labels
            : Array.isArray(trend?.months) &&
                trend.months.length === series.length
              ? trend.months
              : (() => {
                  const anchor = dashboardMonthEnd || new Date();
                  return getLastMonths(series.length).map((d, idx, arr) => {
                    const month = new Date(anchor);
                    month.setMonth(anchor.getMonth() - (arr.length - 1 - idx));
                    return formatMonthLabel(month);
                  });
                })();

        return {
          title: def.title,
          value: normalizeSignedCurrency(valueRaw),
          delta,
          direction,
          series: series.map((v) => normalizeSignedCurrency(v)),
          labels,
        };
      }),
    [advancedMetrics, isYearToggled, dashboardMonthEnd],
  );

  const [openScheduleCallModal, setOpenScheduleCallDemoModal] = useState(false);

  /** Show strategy-call modal once per session, 5 minutes after load (demo / incomplete setup). */
  const SCHEDULE_CALL_MODAL_DELAY_MS = 5 * 60 * 1000;

  useEffect(() => {
    if (!profile || !true_customer_id) return;

    const sessionKey = `schedule_call_shown_${true_customer_id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    const currentCustomer = profile?.customers?.find(
      (c) => String(c.id) === String(true_customer_id),
    );
    if (currentCustomer?.completed_setup_steps?.includes("book_a_call")) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setOpenScheduleCallDemoModal(true);
      sessionStorage.setItem(sessionKey, "true");
    }, SCHEDULE_CALL_MODAL_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, [profile, true_customer_id]);

  const currentCustomer = useMemo(() => {
    const customers = Array.isArray(profile?.customers)
      ? profile.customers
      : [];

    return customers.find((c) => String(c.id) === String(true_customer_id));
  }, [profile?.customers, true_customer_id]);

  const handleCloseDemoModal = () => {
    setOpenScheduleCallDemoModal(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ClientDashboardLayout
        title={null}
        contentMaxWidth="100%"
        contentAlign="left"
      >
        <Box
          sx={{
            px: 0,
            mt: 0,
            maxWidth: "100%",
            width: "100%",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, lg: 3 },
          }}
        >
          {isDemoCustomer &&
            profile?.type !== "admin" &&
            ["Trial Demo", "Unpublished"].includes(
              currentCustomer?.frontend_account_type,
            ) && (
              <>
                <ScheduleCallModal
                  open={openScheduleCallModal}
                  onClose={handleCloseDemoModal}
                  profile={profile}
                  setProfile={setProfile}
                />
                <OnboardingBanner
                  customer_id={true_customer_id}
                  profile={profile}
                  setProfile={setProfile}
                ></OnboardingBanner>
              </>
            )}
          {dashboardLoading ? (
            <PerformanceOverviewSkeleton />
          ) : (
            <>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={0}
                sx={{
                  pb: 2,
                  mb: 0,
                  borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      alignSelf: "stretch",
                      color: "rgba(0, 0, 0, 0.87)",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontFamily: '"Rethink Sans"',
                      fontSize: "1.5rem",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: 1.334,
                    }}
                  >
                    Performance Dashboard
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleExportReport}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.75,
                    background: "#536DEC",
                    fontFamily: '"Rethink Sans"',
                    fontWeight: 600,
                    fontSize: 14,
                    boxShadow:
                      "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.20)",
                    "&:hover": { background: "#4C63D7" },
                  }}
                >
                  Export
                </Button>
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateRows: { xs: "auto auto", lg: "0.2fr 0.8fr" },
                  gap: 3,
                  minHeight: { lg: "calc(100vh - 60px)" },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "4fr 3fr 3fr",
                    },
                    gridTemplateRows: {
                      xs: "auto",
                      md: "auto repeat(2, auto)",
                      lg: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 3,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      gridColumn: { xs: "span 1", md: "1 / -1", lg: "1 / 2" },
                      gridRow: { xs: "span 1", md: "1", lg: "1 / 3" },
                      height: "100%",
                    }}
                  >
                    <PracticeHealthCard summary={practiceHealthData} />
                  </Box>
                  {metricCards.map((metric) => (
                    <Box
                      key={metric.title}
                      sx={{
                        gridColumn: {
                          xs: "span 1",
                          md:
                            metric.title === "Team" ||
                            metric.title === "Operations"
                              ? "1"
                              : "2",
                          lg:
                            metric.title === "Team" ||
                            metric.title === "Operations"
                              ? "2 / 3"
                              : "3 / 4",
                        },
                        gridRow: {
                          xs: "span 1",
                          md:
                            metric.title === "Team" ||
                            metric.title === "Marketing"
                              ? "2"
                              : "3",
                          lg:
                            metric.title === "Team" ||
                            metric.title === "Marketing"
                              ? "1 / 2"
                              : "2 / 3",
                        },
                        alignSelf: "start",
                      }}
                    >
                      <MetricCard
                        {...metric}
                        onClick={() => handleTileClick(metric.title)}
                      />
                    </Box>
                  ))}
                </Box>

                {/* Financial Trend + Activity Pulse: 1 col when Business Health wraps (xs/md), 3-col on lg */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr",
                      lg: showCashOnHand ? "4fr 3fr 3fr" : "1fr",
                    },
                    gap: 3,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      gridColumn: {
                        xs: "span 1",
                        md: "span 1",
                        lg: showCashOnHand ? "1 / 3" : "1 / -1",
                      },
                      minWidth: 0,
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        boxShadow: "#00000013",
                        border: "1px solid #E2E6EF",
                        backgroundColor: "#FFFFFF",
                        height: "100%",
                        minHeight: { xs: "320px", sm: "380px", md: "449px" },
                        borderRadius: "12px",
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          height: "100%",
                          p: { xs: 2, md: "24px" },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: { xs: 1.5, md: 2 } }}
                        >
                          <Typography
                            sx={{
                              color: "#474747",
                              fontFamily: '"Rethink Sans"',
                              fontSize: "20px",
                              fontStyle: "normal",
                              fontWeight: 700,
                              lineHeight: "28.8px",
                              letterSpacing: "0.5px",
                            }}
                          >
                            12-Month Financial Trend
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: {
                              xs: "200px",
                              sm: "240px",
                              md: "291px",
                            },
                            minWidth: 0,
                          }}
                        >
                          <FinancialTrendChart
                            range={trendRange}
                            metricGraphData={metricGraphData}
                            anchorDate={dashboardMonthEnd}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                  {showCashOnHand && (
                    <Box
                      sx={{
                        gridColumn: { xs: "span 1", md: "span 1", lg: "3 / 4" },
                        minWidth: 0,
                      }}
                    >
                      <ActivityPulse data={cashOnHandData} />
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
        {/* Hidden report rendered off-screen for react-to-print */}
        <Box
          sx={{
            position: "absolute",
            top: -9999,
            left: -9999,
            width: "210mm",
            pointerEvents: "none",
            "& *": {
              fontFamily: "'Rethink Sans', Helvetica, sans-serif !important",
            },
          }}
        >
          <PerformanceOverviewReport
            ref={reportRef}
            customerId={customer_id}
            startDate={
              isYearToggled
                ? format(
                    new Date(
                      dashboardMonthEnd.getFullYear(),
                      dashboardMonthEnd.getMonth() - 11,
                      1,
                    ),
                    "yyyy-MM-dd",
                  )
                : format(
                    new Date(
                      dashboardMonthEnd.getFullYear(),
                      dashboardMonthEnd.getMonth(),
                      1,
                    ),
                    "yyyy-MM-dd",
                  )
            }
            endDate={dashboardDateStr}
          />
        </Box>
      </ClientDashboardLayout>
    </ThemeProvider>
  );
};

export default PerformanceOverview;
